export function hexToHsl(hex: string): [number, number, number] {
	const cleaned = hex.replace(/^#/, '');
	const r = parseInt(cleaned.slice(0, 2), 16) / 255;
	const g = parseInt(cleaned.slice(2, 4), 16) / 255;
	const b = parseInt(cleaned.slice(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;

	if (max === min) return [0, 0, l * 100];

	const d = max - min;
	const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
	let h = 0;
	if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
	else if (max === g) h = ((b - r) / d + 2) / 6;
	else h = ((r - g) / d + 4) / 6;

	return [h * 360, s * 100, l * 100];
}

export function hslToHex(h: number, s: number, l: number): string {
	h = ((h % 360) + 360) % 360;
	const sn = Math.max(0, Math.min(100, s)) / 100;
	const ln = Math.max(0, Math.min(100, l)) / 100;

	const c = (1 - Math.abs(2 * ln - 1)) * sn;
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
	const m = ln - c / 2;

	let r = 0,
		g = 0,
		b = 0;
	if (h < 60) {
		r = c;
		g = x;
	} else if (h < 120) {
		r = x;
		g = c;
	} else if (h < 180) {
		g = c;
		b = x;
	} else if (h < 240) {
		g = x;
		b = c;
	} else if (h < 300) {
		r = x;
		b = c;
	} else {
		r = c;
		b = x;
	}

	const toHex = (v: number) =>
		Math.round((v + m) * 255)
			.toString(16)
			.padStart(2, '0');
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// ---------------------------------------------------------------------------
// Color harmony systems
// Each returns [primary, ...complementaries] where primary = the input color.
// ---------------------------------------------------------------------------

export type ColorHarmony =
	| 'triadic' // 3 colors, 120° apart — good all-around balance
	| 'tetradic' // 4 colors, 90° apart — most variety, can be busy
	| 'complementary' // 2 colors, 180° apart — high contrast, simple
	| 'split-complementary' // 3 colors: primary + two flanking the complement (±30°)
	| 'analogous'; // 3 colors, 30° steps — harmonious, lower contrast

export const HARMONY_LABELS: Record<ColorHarmony, string> = {
	triadic: 'Triadic (120°)',
	tetradic: 'Tetradic (90°)',
	complementary: 'Complementary (180°)',
	'split-complementary': 'Split-Complementary',
	analogous: 'Analogous (30°)',
};

export const HARMONIES = Object.keys(HARMONY_LABELS) as ColorHarmony[];

/**
 * Generates a color palette using the given harmony system.
 * Always returns [primary, ...complementaries] with at least 2 colors.
 */
export function generatePalette(hex: string, harmony: ColorHarmony): string[] {
	const [h, s, l] = hexToHsl(hex);
	switch (harmony) {
		case 'triadic':
			return [hex, hslToHex(h + 120, s, l), hslToHex(h + 240, s, l)];
		case 'tetradic':
			return [hex, hslToHex(h + 90, s, l), hslToHex(h + 180, s, l), hslToHex(h + 270, s, l)];
		case 'complementary':
			return [hex, hslToHex(h + 180, s, l)];
		case 'split-complementary':
			return [hex, hslToHex(h + 150, s, l), hslToHex(h + 210, s, l)];
		case 'analogous':
			return [hex, hslToHex(h + 30, s, l), hslToHex(h - 30, s, l)];
	}
}

// Keep existing export for any existing callers.
export function generateTriadicPalette(hex: string): [string, string, string] {
	return generatePalette(hex, 'triadic') as [string, string, string];
}
